/**
 * User store
 */
import { getPersistedValue } from '@/helpers/store.js';
import { getUserMembershipLevel } from '@/components/Base/UserMixin.js';

const sessionMaxAge = 7200000; // 2 hours [msec]
const itemsMaxAge = 129600000; // 36 hours (1.5 days) [msec]

/**
    MembershipLevel for User privileges

        0       Anonymous           not registered
        500     SemiAuthenticated   authentication in process
        1000    BasicUser           registered, unverified, no privileges
        5000    RegisteredUser      registered, base privileges, access to Use userProfile
        9000    ProUser             registered, edit privileges
*/
export const levelAnonymous = 0;
export const levelSemiAuthenticated = 500;
export const levelBasicUser = 1000;
export const levelRegisteredUser = 5000;
export const levelProUser = 9000;

const initialState = () => ({

    userId: 0,
    session: null,
    profile: null,
    accounts: [],
    subscriptions: [],
    offers: [],
    membershipLevel: levelAnonymous,
    saved: [],
    savedTotalCount: 0,
    liked: [],
    likedTotalCount: 0,
    followed: [],
    followedTotalCount: 0,
    shared: [],
    sharedTotalCount: 0,
    timestamps: {}
});

const removeItemFromState = (value, values) => {
    const key = _.head(Object.keys(value));
    const id = key && value[key] && value[key].Id;
    if (id) {
        _.remove(values, (item) => { return (item[key] && item[key].Id) === id; });
    }
};

const createStore = (Vue) => {

    const state = initialState;

    const getters = {

        isAnonymous (state) {
            return !state.session || state.membershipLevel === 0;
        },

        isAuthenticated (state) {
            return !!(state.session && state.membershipLevel >= levelBasicUser);
        },

        getTermsOfServiceAccepted (state) {
            return !!(state.profile && state.profile.TermsOfServiceAccepted);
        },

        getUserId (state) {
            return state.userId;
        },

        getSession (state) {
            return state.session;
        },

        getProfile (state) {
            return state.profile;
        },

        getAccounts (state) {
            return state.accounts;
        },

        getSubscriptions (state) {
            return state.subscriptions;
        },

        getOffers (state) {
            return state.offers;
        },

        getMembershipLevel (state) {
            return state.membershipLevel;
        },

        getMemberships (state) {
            return (state.profile && state.profile.Memberships) || [];
        },

        getUserTools (state) {
            return (state.profile && state.profile.UserTools) || [];
        },

        getSaved (state) {
            return state.saved || [];
        },

        getLiked (state) {
            return state.liked || [];
        },

        getFollowed (state) {
            return state.followed || [];
        },

        getShared (state) {
            return state.shared || [];
        }
    };

    const actions = {

        /**
         * Requests user session from svc/hello.
         */
        async fetchUserSession ({ commit, state }) {
            // check persisted localStorage
            // const value = getPersistedValue(state, 'session', sessionMaxAge);
            // if (value) {
            //     return value;
            // }

            // fetch
            return Vue.prototype.$SVC.getHello()
                .then((response) => {
                    const userInfo = response?.data?.Result;
                    // no commit! session will be evaluated and commited to store later
                    // commit('setSession', session);
                    return userInfo;
                })
                .catch((error) => {
                    commit('clearSession');
                    throw error;
                });
        },

        /**
         * Requests profile Accounts.
         */
        async fetchAccounts ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'accounts', itemsMaxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$SVC.getMyAccounts()
                    .then((response) => {
                        const items = response && response.data && response.data.Results;
                        commit('setAccounts', items);
                        resolve(items);
                    })
                    .catch((error) => {
                        commit('setAccounts', []);
                        reject(error);
                    });
            });
        },

        /**
         * Requests profile Subscriptions.
         */
        async fetchSubscriptions ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'subscriptions', itemsMaxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$SVC.getMySubscriptions()
                    .then((response) => {
                        const items = response && response.data && response.data.Results;
                        commit('setSubscriptions', items);
                        resolve(items);
                    })
                    .catch((error) => {
                        commit('setSubscriptions', []);
                        reject(error);
                    });
            });
        },


        /**
         * Fetch "offers"
         */
        async fetchOffers ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'offers', itemsMaxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                Vue.prototype.$SVC.getMyOffers()
                    .then((response) => {
                        const items = (response && response.data && response.data.Results) || [];
                        commit('setOffers', items);
                        resolve(items);
                    })
                    .catch((error) => {
                        commit('setOffers', []);
                        reject(error);
                    });
            });
        },


        /**
         * Requests profile and profile/items and saves them to store.
         */
        async fetchUserProfile ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'profile', sessionMaxAge);
            if (value) {
                // set membership level, if not already set
                if (getUserMembershipLevel(value) !== state.membershipLevel) {
                    commit('setCurrentMembershiplevel', value);
                }
                return Promise.resolve(value);
            }

            // fetch
            return new Promise((resolve, reject) => {
                // load user profile for Memberships and Tools (Muusa groups)
                Vue.prototype.$SVC.getProfile()
                    .then((response) => {
                        const profile = response && response.data && response.data.Result;
                        commit('setProfile', profile);
                        resolve(profile);
                    })
                    .catch((error) => {
                        commit('clearProfile');
                        reject(error);
                    });
            });
        },

        /**
         * Query user profile items from /svc/profile/items.
         */
        async fetchUserProfileItems ({ dispatch, commit, state }) {
            await dispatch('fetchSaved');
            await dispatch('fetchLiked');
            await dispatch('fetchFollowed');
            return Promise.resolve({
                saved: state.saved,
                liked: state.liked,
                followed: state.followed
            });
        },

        async fetchSaved ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'saved', itemsMaxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            Vue.prototype.$SVC.getProfileItems('saved')
                .then((response) => {
                    const items = response && response.data && response.data.Results;
                    const count = response && response.data && response.data.TotalCount;
                    commit('setSavedItems', {items, count});
                });
        },

        async fetchLiked ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'liked', itemsMaxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return Vue.prototype.$SVC.queryProfileItems('liked')
                .then((response) => {
                    const items = response && response.data && response.data.Results;
                    const count = response && response.data && response.data.TotalCount;
                    commit('setLikedItems', {items, count});
                });
        },

        async fetchFollowed ({ commit, state }) {
            // check persisted localStorage
            const value = getPersistedValue(state, 'followed', itemsMaxAge);
            if (value) {
                return Promise.resolve(value);
            }

            // fetch
            return Vue.prototype.$SVC.queryProfileItems('followed')
                .then((response) => {
                    const items = response && response.data && response.data.Results;
                    const count = response && response.data && response.data.TotalCount;
                    commit('setFollowedItems', {items, count});
                });
        }
    };

    const mutations = {
        RESET (state) {
            const newState = initialState();
            Object.keys(newState).forEach((key) => {
                state[key] = newState[key];
            });
        },

        setUserId (state, value) {
            state.userId = value;
        },

        /**
         * User Session from /svc/hello
         */
        setSession (state, value) {
            state.session = value;
            if (state.membershipLevel === 0) {
                state.membershipLevel = levelSemiAuthenticated; // waiting for full user profile and Memberships
            }
            state.timestamps.session = new Date();
        },

        clearSession (state) {
            state.session = null;
            state.profile = null;
            state.membershipLevel = levelAnonymous;
            state.userId = 0;
            delete state.timestamps.session;
            delete state.timestamps.profile;
        },

        setProfile (state, value) {
            state.profile = value;

            // Do not set default SiteHandle from user.DisplayName (hello response)
            //    if (!state.profile.SiteHandle && state.session && state.session.DisplayName) {
            //        state.profile.SiteHandle = state.session.DisplayName;
            //    }

            // user account memberships are used to deduce the level of privileges
            state.membershipLevel = getUserMembershipLevel(value);

            state.timestamps.profile = new Date();
        },

        clearProfile (state) {
            state.profile = null;
            delete state.timestamps.profile;
        },

        /**
         * Save as `setProfile()`, except without updating timestamp.
         */
        setCurrentMembershiplevel (state) {
            state.membershipLevel = getUserMembershipLevel(state.profile);
        },

        setAccounts (state, value) {
            state.accounts = value;
        },

        setSubscriptions (state, value) {
            state.subscriptions = value;
        },

        setOffers (state, value) {
            state.offers = value;
        },

        setMembershipLevel (state, value) {
            state.membershipLevel = value;
        },

        // SAVED --------------------------------
        setSavedItems (state, value) {
            const { items, count } = value;
            state.saved = items || [];
            state.savedTotalCount = count || 0;
            state.timestamps.saved = new Date();
        },

        addSavedItem (state, value) {
            state.saved.push(value);
            state.savedTotalCount += 1;
        },

        removeSavedItem (state, value) {
            removeItemFromState(value, state.saved);
            if (state.savedTotalCount > 0) {
                state.savedTotalCount -= 1;
            }
        },

        // LIKED --------------------------------
        setLikedItems (state, value) {
            const { items, count } = value;
            state.liked = items || [];
            state.likedTotalCount = count || 0;
            state.timestamps.liked = new Date();
        },

        addLikedItem (state, value) {
            state.liked.push(value);
            state.likedTotalCount += 1;
        },

        removeLikedItem (state, value) {
            removeItemFromState(value, state.liked);
            if (state.likedTotalCount > 0) {
                state.likedTotalCount -= 1;
            }
        },

        // FOLLOWED -----------------------------
        setFollowedItems (state, value) {
            const { items, count } = value;
            state.followed = items || [];
            state.followedTotalCount = count || 0;
            state.timestamps.followed = new Date();
        },

        addFollowedItem (state, value) {
            state.followed.push(value);
            state.followedTotalCount += 1;
        },

        removeFollowedItem (state, value) {
            removeItemFromState(value, state.followed);
            if (state.followedTotalCount > 0) {
                state.followedTotalCount -= 1;
            }
        },

        // SHARED -------------------------------
        setSharedItems (state, value) {
            const { items, count } = value;
            state.shared = items || [];
            state.sharedTotalCount = count || 0;
            state.timestamps.shared = new Date();
        },

        addSharedItem (state, value) {
            state.shared.push(value);
            state.sharedTotalCount += 1;
        },

        removeSharedItem (state, value) {
            removeItemFromState(value, state.shared);
            if (state.sharedTotalCount > 0) {
                state.sharedTotalCount -= 1;
            }
        }
    };

    return {
        namespaced: true,
        state,
        getters,
        actions,
        mutations
    };
};

export default app => createStore(app);
